export type AssistantTextSegmentKind = "thinking" | "response";

export type AssistantTextSegment = {
  kind: AssistantTextSegmentKind;
  text: string;
};

type TagKind = "think" | "final";

type TagMatch = {
  kind: TagKind;
  closing: boolean;
  start: number;
  end: number;
};

export function parseAssistantTextSegments(
  raw: string,
  options: { includeThinking?: boolean } = {},
): AssistantTextSegment[] {
  const trimmed = raw.trim();
  if (!trimmed) {
    return [];
  }
  if (!raw.includes("<")) {
    return [{ kind: "response", text: trimmed }];
  }

  const segments: AssistantTextSegment[] = [];
  let cursor = 0;
  let currentKind: AssistantTextSegmentKind = "response";
  let matchedTag = false;

  while (true) {
    const match = nextTag(raw, cursor);
    if (!match) {
      break;
    }

    matchedTag = true;
    if (match.start > cursor) {
      appendSegment(segments, currentKind, raw.slice(cursor, match.start));
    }

    const tagEnd = raw.indexOf(">", match.end);
    if (tagEnd < 0) {
      cursor = raw.length;
      break;
    }

    const selfClosing = isSelfClosingTag(raw, tagEnd);
    cursor = tagEnd + 1;
    if (selfClosing) {
      continue;
    }

    currentKind = match.closing ? "response" : match.kind === "think" ? "thinking" : "response";
  }

  if (cursor < raw.length) {
    appendSegment(segments, currentKind, raw.slice(cursor));
  }

  if (!matchedTag) {
    return [{ kind: "response", text: trimmed }];
  }

  return options.includeThinking === false
    ? segments.filter((segment) => segment.kind === "response")
    : segments;
}

export function visibleAssistantText(raw: string): string {
  return parseAssistantTextSegments(raw, { includeThinking: false })
    .map((segment) => segment.text)
    .join("\n\n")
    .trim();
}

export function hasVisibleAssistantText(
  raw: string,
  options: { includeThinking?: boolean } = {},
): boolean {
  return (
    parseAssistantTextSegments(raw, {
      includeThinking: options.includeThinking ?? false,
    }).length > 0
  );
}

function nextTag(text: string, from: number): TagMatch | undefined {
  const matches = [
    findTagStart(text, "think", false, from),
    findTagStart(text, "think", true, from),
    findTagStart(text, "final", false, from),
    findTagStart(text, "final", true, from),
  ].filter((match): match is TagMatch => Boolean(match));

  return matches.toSorted((left, right) => left.start - right.start)[0];
}

function findTagStart(
  text: string,
  tag: TagKind,
  closing: boolean,
  from: number,
): TagMatch | undefined {
  const token = closing ? `</${tag}` : `<${tag}`;
  const lowerText = text.toLowerCase();
  let searchStart = from;

  while (searchStart < text.length) {
    const start = lowerText.indexOf(token, searchStart);
    if (start < 0) {
      return undefined;
    }

    const end = start + token.length;
    if (end >= text.length || isTagBoundary(text[end], closing)) {
      return { kind: tag, closing, start, end };
    }

    searchStart = end;
  }

  return undefined;
}

function isTagBoundary(char: string | undefined, closing: boolean): boolean {
  return char === ">" || /\s/u.test(char ?? "") || (!closing && char === "/");
}

function isSelfClosingTag(text: string, tagEnd: number): boolean {
  for (let index = tagEnd - 1; index >= 0; index -= 1) {
    const char = text[index];
    if (/\s/u.test(char ?? "")) {
      continue;
    }
    return char === "/";
  }
  return false;
}

function appendSegment(
  segments: AssistantTextSegment[],
  kind: AssistantTextSegmentKind,
  text: string,
) {
  const trimmed = text.trim();
  if (trimmed) {
    segments.push({ kind, text: trimmed });
  }
}
